<template>
  <v-container class="payment-proof-page py-7">
    <div class="payment-proof-page__header">
      <div>
        <v-breadcrumbs class="app-breadcrumb pa-0 mb-2" divider="›" :items="breadcrumbs"></v-breadcrumbs>
        <h1 class="payment-proof-page__title">Comprovantes de pagamento</h1>
        <p class="payment-proof-page__subtitle">Envie e acompanhe os comprovantes de pagamento aos credores.</p>
      </div>
      <div class="payment-proof-page__actions">
        <v-btn color="primary" elevation="0" @click="openDialog">
          <v-icon left>mdi-plus</v-icon>
          Novo comprovante
        </v-btn>
        <v-btn color="primary" outlined elevation="0" to="/comprovantes-pagamento/importar">
          <v-icon left>mdi-file-excel-outline</v-icon>
          Importar em lote
        </v-btn>
      </div>
    </div>

    <div class="payment-proof-page__summary">
      <button v-for="card in summaryCards" :key="card.key" type="button"
        class="payment-proof-page__summary-card" :class="{ 'payment-proof-page__summary-card--active': card.active }"
        @click="applySummary(card.key)">
        <span class="payment-proof-page__summary-label">
          <span class="payment-proof-page__summary-dot" :style="{ backgroundColor: card.color }" />
          {{ card.label }}
        </span>
        <strong>{{ card.value }}</strong>
      </button>
    </div>

    <v-row v-if="lastInconsistency">
      <v-col cols="12">
        <v-alert type="warning" outlined>{{ lastInconsistency }}</v-alert>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12">
        <v-card outlined class="table-card payment-proof-page__card">
          <div class="payment-proof-page__toolbar">
            <v-text-field dense outlined hide-details clearable
              prepend-inner-icon="mdi-magnify" label="Buscar por CPF/CNPJ, processo ou responsável"
              v-model="filter.query" @keyup.enter="load(true)" @click:clear="load(true)" />
            <v-select dense outlined hide-details clearable label="Situação"
              :items="dataset.statuses" item-value="id" item-text="name"
              v-model="filter.status" @change="applyStatusFilter" />
            <v-btn color="primary" elevation="0" @click="load(true)">Buscar</v-btn>
          </div>
          <v-card-text>
            <v-alert v-if="loadError" type="error" outlined>
              Não foi possível carregar os comprovantes.
              <v-btn text color="error" @click="load">Tentar novamente</v-btn>
            </v-alert>
            <v-data-table :headers="headers" :items="items" :server-items-length="total"
              :options.sync="options" :loading="loading" :disable-sort="true"
              :footer-props="footerOptions" class="app-table hover-table"
              no-data-text="Nenhum comprovante enviado" loading-text="Carregando dados...">
              <template v-slot:[`item.status`]="{ item }">
                <v-chip small :color="statusColor(item.status)" dark>
                  {{ item.status | enum(dataset.statuses, "id", "name") }}
                </v-chip>
              </template>
              <template v-slot:[`item.creditor`]="{ item }">
                <div class="payment-proof-page__creditor">
                  <strong>{{ item.creditor_name || item.name || "Credor" }}</strong>
                  <span>{{ formatRegistration(item.creditor_registration) }}</span>
                </div>
              </template>
              <template v-slot:[`item.document_type`]="{ item }">
                {{ item.document_type | enum(dataset.documentTypes, "id", "name") }}
              </template>
              <template v-slot:[`item.paid_amount`]="{ item }">
                {{ formatCurrency(item.paid_amount) }}
              </template>
              <template v-slot:[`item.submitted_at`]="{ item }">
                {{ item.submitted_at | defaultDate("DD/MM/YYYY HH:mm") }}
              </template>
              <template v-slot:[`item.payment_date`]="{ item }">
                {{ item.payment_date | defaultDate("DD/MM/YYYY") }}
              </template>
              <template v-slot:[`item.has_inconsistency`]="{ item }">
                <v-tooltip bottom v-if="item.has_inconsistency">
                  <template #activator="{ on }">
                    <v-icon v-on="on" color="warning">mdi-alert-circle-outline</v-icon>
                  </template>
                  <span>{{ item.inconsistency_reason }}</span>
                </v-tooltip>
                <span v-else>Não</span>
              </template>
              <template v-slot:[`item.actions`]="{ item }">
                <v-btn icon color="primary" @click="viewDetails(item)">
                  <v-icon>mdi-eye-outline</v-icon>
                </v-btn>
              </template>
              <template v-slot:no-data>
                <div class="payment-proof-page__empty">
                  <v-icon size="44" color="#98A2B3">mdi-file-document-outline</v-icon>
                  <strong>Nenhum comprovante encontrado</strong>
                  <span>Envie um novo comprovante ou ajuste os filtros da busca.</span>
                </div>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-navigation-drawer v-model="dialogForm" fixed temporary right :width="drawerWidth"
      class="payment-proof-page__drawer">
      <div class="payment-proof-page__drawer-shell">
        <header class="payment-proof-page__dialog-title">
          <div>
            <h2>Novo comprovante</h2>
            <p>Envie um comprovante de pagamento vinculado a um credor do quadro geral.</p>
          </div>
          <v-btn icon outlined class="payment-proof-page__close" @click="closeDialog" aria-label="Fechar">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </header>
        <main class="payment-proof-page__drawer-body">
          <v-form ref="form" v-model="validForm">
            <div class="payment-proof-page__section-title">
              <span>1</span><strong>Credor e pagamento</strong><i />
            </div>
            <v-row>
              <v-col cols="12">
                <v-autocomplete dense outlined hide-details="auto" label="Credor"
                  hint="Digite pelo menos 3 caracteres. A busca pode ser feita por CPF/CNPJ ou nome completo."
                  persistent-hint prepend-inner-icon="mdi-magnify" :items="creditorItems"
                  v-model="selectedCreditor" :search-input.sync="searchCreditor"
                  :loading="loadingCreditors" :filter="creditorFilter"
                  item-value="id" item-text="name" return-object
                  no-data-text="Nenhum credor encontrado" :rules="[rules.required]"
                  @update:search-input="loadCreditors">
                  <template v-slot:item="data">
                    <div class="py-1">
                      <div>{{ data.item.name }}</div>
                      <small class="grey--text"><strong>{{ formatRegistration(data.item.registration) }}</strong></small>
                    </div>
                  </template>
                  <template v-slot:selection="data">
                    <span>{{ data.item.name }} - {{ formatRegistration(data.item.registration) }}</span>
                  </template>
                </v-autocomplete>
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12" sm="4">
                <v-currency-field dense outlined hide-details="auto" label="Valor pago"
                  v-model="form.paid_amount" :allow-negative="false"
                  :rules="[rules.required, positiveAmountRule]">
                </v-currency-field>
              </v-col>
              <v-col cols="12" sm="4">
                <v-text-field dense outlined hide-details="auto" type="date" label="Data do pagamento"
                  v-model="form.payment_date" :rules="[rules.required]">
                </v-text-field>
              </v-col>
              <v-col cols="12" sm="4">
                <v-select dense outlined hide-details="auto" label="Forma ou motivo do pagamento"
                  :items="dataset.paymentReasons" item-value="id" item-text="name"
                  v-model="form.payment_reason" :rules="[rules.required]">
                </v-select>
              </v-col>
            </v-row>

            <div class="payment-proof-page__section-title">
              <span>2</span><strong>Responsável pelo pagamento</strong><i />
            </div>
            <v-row>
              <v-col cols="12" sm="4">
                <v-select dense outlined hide-details="auto" label="Tipo de pagador"
                  :items="dataset.payerTypes" item-value="id" item-text="name"
                  v-model="form.payer_type" :rules="[rules.required]">
                </v-select>
              </v-col>
              <v-col cols="12" sm="8">
                <v-text-field dense outlined hide-details="auto" label="Responsável pelo pagamento"
                  v-model="form.payment_responsible" :rules="[rules.required]">
                </v-text-field>
              </v-col>
              <v-col cols="12" sm="4" v-if="form.payer_type !== 'creditor'">
                <v-autocomplete dense outlined hide-details="auto" label="Empresa/pagador no QGC"
                  hint="Busque por CNPJ ou razão social quando o pagamento não for feito pelo próprio credor."
                  persistent-hint prepend-inner-icon="mdi-domain" :items="payerItems"
                  v-model="selectedPayer" :search-input.sync="searchPayer"
                  :loading="loadingPayers" :filter="creditorFilter"
                  item-value="id" item-text="name" return-object clearable
                  no-data-text="Nenhum pagador encontrado" @update:search-input="loadPayers">
                  <template v-slot:item="data">
                    <div class="py-1">
                      <div>{{ data.item.name }}</div>
                      <small class="grey--text"><strong>{{ formatRegistration(data.item.registration) }}</strong></small>
                    </div>
                  </template>
                  <template v-slot:selection="data">
                    <span>{{ data.item.name }} - {{ formatRegistration(data.item.registration) }}</span>
                  </template>
                </v-autocomplete>
              </v-col>
              <v-col cols="12" sm="4" v-if="form.payer_type !== 'creditor'">
                <v-text-field dense outlined hide-details="auto" label="CNPJ/CPF do pagador"
                  v-model="form.payer_registration" :rules="[rules.required]">
                </v-text-field>
              </v-col>
            </v-row>

            <div class="payment-proof-page__section-title">
              <span>3</span><strong>Processo e documento</strong><i />
            </div>
            <v-row>
              <v-col cols="12" sm="6">
                <v-text-field dense outlined hide-details="auto" label="Processo relacionado (opcional)"
                  v-model="form.process_number">
                </v-text-field>
              </v-col>
              <v-col cols="12" sm="6">
                <v-select dense outlined hide-details="auto" label="Documento comprobatório"
                  :items="dataset.documentTypes" item-value="id" item-text="name"
                  v-model="form.document_type" :rules="[rules.required]">
                </v-select>
              </v-col>
            </v-row>

            <div class="payment-proof-page__section-title">
              <span>4</span><strong>Arquivo do comprovante</strong><i />
            </div>
            <input ref="documentInput" class="payment-proof-page__native-file" type="file"
              accept="image/png,image/jpeg,application/pdf" @change="handleDocumentChange" />
            <div v-if="!form.document" class="payment-proof-page__dropzone"
              role="button" tabindex="0"
              @click="$refs.documentInput.click()"
              @keydown.enter="$refs.documentInput.click()"
              @dragover.prevent @drop.prevent="handleDocumentDrop">
              <span class="payment-proof-page__dropzone-icon">
                <v-icon color="primary" size="28">mdi-cloud-upload-outline</v-icon>
              </span>
              <div>
                <strong>Arraste o arquivo aqui ou clique para selecionar</strong>
                <p>PDF, PNG ou JPG com até 20 MB</p>
              </div>
            </div>
            <div v-else class="payment-proof-page__file-card">
              <span class="payment-proof-page__file-icon">
                <v-icon color="primary">mdi-file-document-outline</v-icon>
              </span>
              <div class="payment-proof-page__file-data">
                <strong>{{ form.document.name }}</strong>
                <span>{{ formatFileSize(form.document.size) }} · pronto para envio</span>
              </div>
              <v-btn text small color="primary" @click="$refs.documentInput.click()">Substituir</v-btn>
              <v-btn icon small color="error" @click="clearDocument" aria-label="Remover arquivo">
                <v-icon small>mdi-delete-outline</v-icon>
              </v-btn>
            </div>

            <div v-if="selectedCreditor && form.paid_amount && form.document"
              class="payment-proof-page__review">
              <div class="payment-proof-page__review-title">
                <v-icon color="primary" small>mdi-check-circle-outline</v-icon>
                Confira antes de enviar
              </div>
              <div class="payment-proof-page__review-grid">
                <div><span>Credor</span><strong>{{ selectedCreditor.name }}</strong></div>
                <div><span>Valor</span><strong>{{ formatCurrency(parseCurrencyAmount(form.paid_amount)) }}</strong></div>
                <div><span>Data</span><strong>{{ formatDate(form.payment_date) }}</strong></div>
                <div><span>Documento</span><strong>{{ form.document.name }}</strong></div>
              </div>
            </div>
          </v-form>
        </main>
        <footer class="payment-proof-page__drawer-footer">
          <v-btn outlined color="primary" elevation="0" @click="closeDialog">Cancelar</v-btn>
          <v-btn color="primary" elevation="0" :loading="saving" @click="submit">
            Enviar comprovante
          </v-btn>
        </footer>
      </div>
    </v-navigation-drawer>

    <v-navigation-drawer v-model="dialogDetails" fixed temporary right :width="drawerWidth">
      <v-card flat min-height="100%">
        <v-card-title class="payment-proof-page__dialog-title">
          <div>
            <div>Detalhes do comprovante</div>
            <small class="font-weight-regular grey--text">Informações registradas no envio.</small>
          </div>
          <v-spacer />
          <v-btn icon @click="dialogDetails = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-card-text class="pt-6">
          <v-skeleton-loader v-if="loadingDetails" type="article" />
          <template v-else>
            <v-row>
              <v-col cols="12" sm="6"><strong>CPF/CNPJ do credor</strong><div>{{ formatRegistration(details.creditor_registration) }}</div></v-col>
              <v-col cols="12" sm="6"><strong>Situação</strong><div>{{ details.status | enum(dataset.statuses, "id", "name") }}</div></v-col>
              <v-col cols="12" sm="6"><strong>Valor pago</strong><div>{{ formatCurrency(details.paid_amount) }}</div></v-col>
              <v-col cols="12" sm="6"><strong>Data do pagamento</strong><div>{{ details.payment_date | defaultDate("DD/MM/YYYY") }}</div></v-col>
              <v-col cols="12" sm="6"><strong>Processo relacionado</strong><div>{{ details.process_number || "Não informado" }}</div></v-col>
              <v-col cols="12" sm="6"><strong>Responsável</strong><div>{{ details.payment_responsible || "-" }}</div></v-col>
            </v-row>
            <v-alert v-if="details.has_inconsistency" type="warning" outlined dense class="mt-3">
              {{ details.inconsistency_reason }}
            </v-alert>
            <div class="font-weight-bold mt-4 mb-2">Documentos</div>
            <div class="payment-proof-page__documents">
              <button v-for="(document, index) in details.documents || []" :key="document.id || index"
                type="button" class="payment-proof-page__document"
                :class="{ 'payment-proof-page__document--active': selectedDocumentIndex === index }"
                @click="selectedDocumentIndex = index">
                <v-icon color="primary">mdi-file-document-outline</v-icon>
                <span>{{ document.document_name || `Documento ${index + 1}` }}</span>
              </button>
            </div>
            <div v-if="selectedDocument" class="payment-proof-page__preview mt-4">
              <img v-if="isImageDocument(selectedDocument)" :src="selectedDocument.document_url"
                alt="Pré-visualização do comprovante" />
              <iframe v-else-if="isPdfDocument(selectedDocument)" :src="selectedDocument.document_url"
                title="Pré-visualização do comprovante" />
              <div v-else class="payment-proof-page__preview-fallback">
                <v-icon size="46" color="#98A2B3">mdi-file-document-outline</v-icon>
                <span>Pré-visualização indisponível para este formato.</span>
              </div>
            </div>
            <v-btn v-if="selectedDocument" class="mt-3" outlined color="primary"
              :href="selectedDocument.document_url" target="_blank">
              <v-icon left>mdi-open-in-new</v-icon>Abrir documento
            </v-btn>
          </template>
        </v-card-text>
      </v-card>
    </v-navigation-drawer>
  </v-container>
</template>

<script>
import axios from "axios";
import { debounce } from "lodash";
import paymentProofStatuses from "@/common/data/payment-proof-status.data";
import paymentProofDocumentTypes from "@/common/data/payment-proof-document-types.data";

export default {
  name: "PaymentProofList",
  data: () => ({
    breadcrumbs: [
      { text: "Homepage", disabled: false, to: "/home", exact: true },
      { text: "Comprovantes de pagamento", disabled: true, exact: true },
    ],
    dataset: {
      statuses: paymentProofStatuses,
      documentTypes: paymentProofDocumentTypes,
      paymentReasons: [
        { id: "pix", name: "Pix" },
        { id: "deposit", name: "Depósito" },
        { id: "judicial_block", name: "Bloqueio judicial" },
        { id: "court_permit", name: "Alvará" },
        { id: "agreement", name: "Acordo" },
        { id: "other", name: "Outro motivo" },
      ],
      payerTypes: [
        { id: "creditor", name: "Próprio credor" },
        { id: "recovering_company", name: "Recuperanda" },
        { id: "partner", name: "Sócio" },
        { id: "other_company", name: "Outra empresa" },
        { id: "other", name: "Outro terceiro" },
      ],
    },
    dialogForm: false,
    validForm: true,
    form: {},
    selectedCreditor: null,
    selectedPayer: null,
    searchCreditor: "",
    searchPayer: "",
    creditorItems: [],
    payerItems: [],
    loadingCreditors: false,
    loadingPayers: false,
    rules: {},
    saving: false,
    loading: false,
    loadError: false,
    summary: {
      total: 0,
      in_review: 0,
      validated: 0,
      rejected: 0,
      inconsistent: 0,
    },
    lastInconsistency: "",
    dialogDetails: false,
    loadingDetails: false,
    details: {},
    selectedDocumentIndex: 0,
    items: [],
    total: 0,
    options: { page: 1, itemsPerPage: 10 },
    footerOptions: {
      itemsPerPageText: "Itens por página:",
      itemsPerPageOptions: [10, 20, 30, 40, 50],
      pageText: "{0}-{1} de {2}",
    },
    headers: [
      { text: "Status", value: "status" },
      { text: "Credor", value: "creditor" },
      { text: "Processo", value: "process_number" },
      { text: "Valor", value: "paid_amount" },
      { text: "Data do pagamento", value: "payment_date" },
      { text: "Tipo", value: "document_type" },
      { text: "Inconsistência", value: "has_inconsistency" },
      { text: "Enviado em", value: "submitted_at" },
      { text: "", value: "actions", sortable: false, align: "end" },
    ],
    filter: {
      query: "",
      status: null,
      pending: false,
    },
  }),
  computed: {
    drawerWidth() {
      return this.$vuetify.breakpoint.smAndDown ? "100%" : 760;
    },
    selectedDocument() {
      const documents = this.details.documents || [];
      return documents[this.selectedDocumentIndex] || null;
    },
    summaryCards() {
      return [
        { key: "all", label: "Todos", value: this.summary.total, color: "#98A2B3", active: !this.filter.status && !this.filter.pending },
        { key: "pending", label: "Em análise", value: this.summary.in_review, color: "#2A3070", active: this.filter.pending },
        { key: "validated", label: "Aprovados", value: this.summary.validated, color: "#1E6B34", active: this.filter.status === "validated" },
        { key: "rejected", label: "Recusados", value: this.summary.rejected, color: "#A32020", active: this.filter.status === "rejected" },
        { key: "inconsistent", label: "Inconsistências", value: this.summary.inconsistent, color: "#C77A17", active: this.filter.status === "inconsistent" },
      ];
    },
  },
  watch: {
    options: {
      handler() {
        this.load();
      },
      deep: true,
    },
    selectedPayer() {
      this.syncSelectedPayer();
    },
  },
  created() {
    this.rules = this.$rules;
    this.resetForm();
    this.load();
  },
  methods: {
    openDialog() {
      this.resetForm();
      this.dialogForm = true;
    },
    closeDialog() {
      this.dialogForm = false;
      this.resetForm();
    },
    resetForm() {
      this.form = {
        paid_amount: null,
        payment_date: "",
        payment_reason: null,
        payer_type: "creditor",
        payer_registration: "",
        payment_responsible: "",
        process_number: "",
        document_type: null,
        document: null,
      };
      this.selectedCreditor = null;
      this.selectedPayer = null;
      this.searchCreditor = "";
      this.searchPayer = "";
      this.creditorItems = [];
      this.payerItems = [];

      this.$nextTick(() => {
        if (this.$refs.form) this.$refs.form.resetValidation();
        if (this.$refs.documentInput) this.$refs.documentInput.value = "";
      });
    },
    loadCreditors: debounce(async function (search) {
      if (!search || search.length < 3) return;

      this.loadingCreditors = true;

      try {
        const response = await axios.get("/api/client/users-creditors/qgc-creditor-request-list", {
          params: { search },
        });
        this.creditorItems = Array.isArray(response.data.content) ? response.data.content : [];
      } catch (error) {
        this.$dialog.notify.error("Falha ao carregar credores. Tente novamente.", { position: "top-right", timeout: 2000 });
      } finally {
        this.loadingCreditors = false;
      }
    }, 300),
    loadPayers: debounce(async function (search) {
      if (!search || search.length < 3) return;

      this.loadingPayers = true;

      try {
        const response = await axios.get("/api/client/users-creditors/qgc-creditor-request-list", {
          params: { search },
        });
        this.payerItems = Array.isArray(response.data.content) ? response.data.content : [];
      } catch (error) {
        this.$dialog.notify.error("Falha ao carregar pagadores. Tente novamente.", { position: "top-right", timeout: 2000 });
      } finally {
        this.loadingPayers = false;
      }
    }, 300),
    creditorFilter(item, queryText) {
      const name = String(item.name || "").toLowerCase();
      const registration = String(item.registration || "").toLowerCase();
      const query = String(queryText || "").toLowerCase();
      return name.includes(query) || registration.includes(query.replace(/\D/g, ""));
    },
    parseCurrencyAmount(value) {
      if (typeof value === "number") return value;

      const normalized = String(value || "")
        .replace(/[^\d,.-]/g, "")
        .replace(/\./g, "")
        .replace(",", ".");

      return Number(normalized);
    },
    positiveAmountRule(value) {
      return this.parseCurrencyAmount(value) > 0 || "Valor inválido";
    },
    async submit() {
      if (!this.$refs.form.validate()) return;
      if (!this.form.document) {
        this.$dialog.notify.warning("Selecione o arquivo do comprovante.", { position: "top-right", timeout: 2000 });
        return;
      }

      this.saving = true;
      this.lastInconsistency = "";

      const payload = new FormData();
      payload.append("creditor_registration", this.selectedCreditor.registration);
      payload.append("paid_amount", this.parseCurrencyAmount(this.form.paid_amount));
      payload.append("payment_date", this.form.payment_date);
      payload.append("payment_reason", this.form.payment_reason);
      payload.append("payment_responsible", this.form.payment_responsible);
      payload.append("payer_name", this.form.payment_responsible);
      payload.append("payer_type", this.form.payer_type);
      payload.append("document_type", this.form.document_type);
      payload.append("document", this.form.document);
      if (this.form.payer_type !== "creditor") {
        payload.append("payer_registration", this.selectedPayer ? this.selectedPayer.registration : this.form.payer_registration);
        payload.append("third_party_type", this.form.payer_type);
      }
      if (this.form.process_number) payload.append("process_number", this.form.process_number);

      try {
        const result = await axios.post("/api/client/payment-proofs", payload);
        const content = result.data.content;
        if (content.has_inconsistency) {
          this.lastInconsistency = content.inconsistency_reason;
          this.$dialog.notify.warning("Comprovante enviado com inconsistência para análise.", { position: "top-right", timeout: 3000 });
        } else {
          this.$dialog.notify.info("Comprovante enviado com sucesso.", { position: "top-right", timeout: 2000 });
        }
        this.closeDialog();
        await this.load(true);
      } catch (error) {
        this.$dialog.notify.error(this.$codes(error), { position: "top-right", timeout: 2000 });
      } finally {
        this.saving = false;
      }
    },
    async load(resetPage = false) {
      if (resetPage) this.options.page = 1;
      this.loading = true;
      this.loadError = false;
      try {
        const result = await axios.get("/api/client/payment-proofs", {
          params: {
            page: this.options.page,
            limit: this.options.itemsPerPage,
            search: {
              query: this.filter.query,
              status: this.filter.status,
            },
          },
        });
        this.items = result.data.content.data;
        this.total = result.data.content.paging.total;
        this.summary = result.data.content.summary || this.summary;
      } catch (error) {
        this.loadError = true;
        this.$dialog.notify.error(this.$codes(error), { position: "top-right", timeout: 2000 });
      } finally {
        this.loading = false;
      }
    },
    applySummary(key) {
      this.filter.status = null;
      this.filter.pending = false;
      if (key === "pending") this.filter.pending = true;
      if (["validated", "rejected", "inconsistent"].includes(key)) this.filter.status = key;
      this.load(true);
    },
    applyStatusFilter() {
      this.filter.pending = false;
      this.load(true);
    },
    async viewDetails(item) {
      this.dialogDetails = true;
      this.loadingDetails = true;
      this.details = {};
      this.selectedDocumentIndex = 0;
      try {
        const result = await axios.get(`/api/client/payment-proofs/${item.id}`);
        this.details = result.data.content;
      } catch (error) {
        this.dialogDetails = false;
        this.$dialog.notify.error(this.$codes(error), { position: "top-right", timeout: 2000 });
      } finally {
        this.loadingDetails = false;
      }
    },
    isImageDocument(document) {
      return String(document.document_mime || "").startsWith("image/");
    },
    isPdfDocument(document) {
      return String(document.document_mime || "").includes("pdf") ||
        String(document.document_name || "").toLowerCase().endsWith(".pdf");
    },
    formatCurrency(value) {
      return Number(value || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
    },
    formatDate(value) {
      if (!value) return "Não informada";
      const parts = String(value).slice(0, 10).split("-");
      return parts.length === 3 ? `${parts[2]}/${parts[1]}/${parts[0]}` : value;
    },
    formatFileSize(value) {
      const bytes = Number(value || 0);
      if (!bytes) return "0 KB";
      if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
      return `${(bytes / (1024 * 1024)).toFixed(1).replace(".", ",")} MB`;
    },
    handleDocumentChange(event) {
      const file = event.target.files && event.target.files[0];
      if (!file) return;
      if (file.size > 20 * 1024 * 1024) {
        this.$dialog.notify.warning("O arquivo deve ter no máximo 20 MB.", { position: "top-right", timeout: 2000 });
        event.target.value = "";
        return;
      }
      this.form.document = file;
    },
    handleDocumentDrop(event) {
      const file = event.dataTransfer.files && event.dataTransfer.files[0];
      if (!file) return;
      if (!["application/pdf", "image/png", "image/jpeg"].includes(file.type)) {
        this.$dialog.notify.warning("Envie um arquivo PDF, PNG ou JPG.", { position: "top-right", timeout: 2000 });
        return;
      }
      if (file.size > 20 * 1024 * 1024) {
        this.$dialog.notify.warning("O arquivo deve ter no máximo 20 MB.", { position: "top-right", timeout: 2000 });
        return;
      }
      this.form.document = file;
    },
    clearDocument() {
      this.form.document = null;
      if (this.$refs.documentInput) this.$refs.documentInput.value = "";
    },
    formatRegistration(value) {
      const digits = String(value || "").replace(/\D/g, "");
      if (digits.length === 11) return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
      if (digits.length === 14) return digits.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, "$1.$2.$3/$4-$5");
      return value || "-";
    },
    syncSelectedPayer() {
      if (!this.selectedPayer) return;
      this.form.payment_responsible = this.selectedPayer.name || this.form.payment_responsible;
      this.form.payer_registration = this.selectedPayer.registration || this.form.payer_registration;
    },
    statusColor(status) {
      return {
        submitted: "primary",
        in_review: "info",
        inconsistent: "warning",
        validated: "success",
        rejected: "error",
      }[status] || "grey";
    },
  },
};
</script>

<style scoped>
.payment-proof-page {
  max-width: 1320px;
}

.payment-proof-page__header,
.payment-proof-page__actions,
.payment-proof-page__toolbar {
  display: flex;
  align-items: center;
}

.payment-proof-page__actions {
  gap: 10px;
}

.payment-proof-page__header {
  justify-content: space-between;
  gap: 24px;
  margin-bottom: 24px;
}

.payment-proof-page__title {
  color: #101828;
  font-size: 28px;
  line-height: 1.25;
}

.payment-proof-page__subtitle {
  margin: 6px 0 0;
  color: #667085;
}

.payment-proof-page__card {
  overflow: hidden;
  border-color: #e4e7ec !important;
  border-radius: 10px;
}

.payment-proof-page__summary {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 18px;
}

.payment-proof-page__summary-card {
  display: flex;
  min-height: 82px;
  flex-direction: column;
  align-items: stretch;
  justify-content: space-between;
  gap: 8px;
  padding: 13px 15px;
  border: 1px solid #e4e7ec;
  border-radius: 9px;
  background: #fff;
  color: #475467;
  text-align: left;
}

.payment-proof-page__summary-card--active {
  border-color: #c7d0f2;
  box-shadow: 0 1px 4px rgba(42, 48, 112, 0.12);
}

.payment-proof-page__summary-card strong {
  color: #101828;
  font-size: 22px;
  line-height: 1;
}

.payment-proof-page__summary-label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
}

.payment-proof-page__summary-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
}

.payment-proof-page__empty {
  display: flex;
  min-height: 220px;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 8px;
  color: #667085;
}

.payment-proof-page__documents {
  display: grid;
  gap: 8px;
}

.payment-proof-page__document {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 11px 12px;
  border: 1px solid #e4e7ec;
  border-radius: 8px;
  background: #fff;
  color: #344054;
  text-align: left;
}

.payment-proof-page__document--active {
  border-color: #2a3070;
  background: #f7f8fc;
}

.payment-proof-page__preview {
  overflow: hidden;
  height: 420px;
  border: 1px solid #e4e7ec;
  border-radius: 8px;
  background: #f7f8fa;
}

.payment-proof-page__preview img,
.payment-proof-page__preview iframe {
  width: 100%;
  height: 100%;
  border: 0;
  object-fit: contain;
}

.payment-proof-page__preview-fallback {
  display: flex;
  height: 100%;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 10px;
  color: #667085;
}

.payment-proof-page__toolbar {
  display: grid;
  grid-template-columns: minmax(280px, 1fr) 220px auto;
  gap: 12px;
  padding: 18px 20px;
  border-bottom: 1px solid #e4e7ec;
  background: #fafbfc;
}

.payment-proof-page__creditor {
  display: flex;
  min-width: 170px;
  flex-direction: column;
  gap: 2px;
}

.payment-proof-page__creditor span {
  color: #667085;
  font-size: 12px;
}

.payment-proof-page__drawer ::v-deep .v-navigation-drawer__content {
  overflow: hidden;
}

.payment-proof-page__drawer-shell {
  display: flex;
  height: 100vh;
  flex-direction: column;
  background: #fff;
}

.payment-proof-page__dialog-title {
  display: flex;
  flex: 0 0 auto;
  align-items: flex-start;
  justify-content: space-between;
  gap: 18px;
  padding: 18px 22px;
  border-bottom: 1px solid #e4e7ec;
}

.payment-proof-page__dialog-title h2 {
  margin: 0;
  color: #101828;
  font-size: 19px;
  font-weight: 700;
  line-height: 1.3;
  text-transform: none;
}

.payment-proof-page__dialog-title p {
  margin: 4px 0 0;
  color: #667085;
  font-size: 12.5px;
  font-weight: 400;
  letter-spacing: 0;
  line-height: 1.45;
  text-transform: none;
}

.payment-proof-page__close {
  width: 32px !important;
  min-width: 32px !important;
  height: 32px !important;
  flex: 0 0 32px;
  border-color: #d0d5dd;
  border-radius: 7px;
}

.payment-proof-page__drawer-body {
  flex: 1 1 auto;
  overflow-y: auto;
  padding: 2px 22px 26px;
}

.payment-proof-page__drawer-footer {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  padding: 15px 22px;
  border-top: 1px solid #e4e7ec;
  background: #fff;
  box-shadow: 0 -5px 18px rgba(16, 24, 40, 0.04);
}

.payment-proof-page__section-title {
  display: flex;
  align-items: center;
  gap: 9px;
  margin: 20px 0 13px;
  color: #101828;
  font-size: 14px;
  font-weight: 600;
}

.payment-proof-page__section-title span {
  display: inline-flex;
  width: 22px;
  height: 22px;
  flex: 0 0 22px;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: #eef1fb;
  color: #2a3070;
  font-size: 12px;
  font-weight: 700;
}

.payment-proof-page__section-title i {
  height: 1px;
  flex: 1 1 auto;
  background: #e4e7ec;
}

.payment-proof-page__native-file {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
}

.payment-proof-page__dropzone {
  display: flex;
  min-height: 132px;
  align-items: center;
  justify-content: center;
  gap: 14px;
  padding: 22px;
  border: 1.5px dashed #aab2c8;
  border-radius: 9px;
  background-color: #fbfcfe;
  background-image: repeating-linear-gradient(135deg, rgba(42, 48, 112, 0.018) 0, rgba(42, 48, 112, 0.018) 8px, transparent 8px, transparent 16px);
  cursor: pointer;
  text-align: left;
}

.payment-proof-page__dropzone-icon,
.payment-proof-page__file-icon {
  display: inline-flex;
  width: 48px;
  height: 48px;
  flex: 0 0 48px;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  background: #eef1fb;
}

.payment-proof-page__dropzone strong {
  color: #20263b;
  font-size: 14px;
}

.payment-proof-page__dropzone p {
  margin: 4px 0 0;
  color: #667085;
  font-size: 12px;
}

.payment-proof-page__file-card {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 13px;
  border: 1px solid #d9deeb;
  border-radius: 9px;
  background: #fbfcfe;
}

.payment-proof-page__file-data {
  display: flex;
  min-width: 0;
  flex: 1 1 auto;
  flex-direction: column;
  gap: 3px;
}

.payment-proof-page__file-data strong {
  overflow: hidden;
  color: #20263b;
  font-size: 13px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.payment-proof-page__file-data span {
  color: #667085;
  font-size: 12px;
}

.payment-proof-page__review {
  margin-top: 18px;
  padding: 14px;
  border: 1px solid #d9deeb;
  border-radius: 9px;
  background: #f8f9fd;
}

.payment-proof-page__review-title {
  display: flex;
  align-items: center;
  gap: 7px;
  margin-bottom: 12px;
  color: #20263b;
  font-size: 13px;
  font-weight: 700;
}

.payment-proof-page__review-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 11px 18px;
}

.payment-proof-page__review-grid > div {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 2px;
}

.payment-proof-page__review-grid span {
  color: #667085;
  font-size: 11px;
}

.payment-proof-page__review-grid strong {
  overflow: hidden;
  color: #20263b;
  font-size: 12.5px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

@media (max-width: 800px) {
  .payment-proof-page__header {
    align-items: flex-start;
    flex-direction: column;
  }

  .payment-proof-page__actions {
    width: 100%;
    flex-wrap: wrap;
  }

  .payment-proof-page__toolbar {
    grid-template-columns: 1fr;
  }

  .payment-proof-page__summary {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .payment-proof-page__drawer-body {
    padding-right: 16px;
    padding-left: 16px;
  }

  .payment-proof-page__drawer-footer {
    padding-right: 16px;
    padding-left: 16px;
  }

  .payment-proof-page__review-grid {
    grid-template-columns: 1fr;
  }
}
</style>
